import { useTemplateUpdateCheck } from '../hooks/useTemplateUpdateCheck';

/**
 * Full-width banner for the Workspace header. Mirrors the shape of
 * `<UpdateBanner>` (the electron-updater app-update banner) so the two
 * visually stack when both are active.
 *
 * Click `Update now` → parent-provided handler opens the managed
 * update flow. `Check for updates` forces a cache refresh so the user
 * can manually re-poll without waiting for the 5-min staleTime.
 */
export function TemplateUpdateBanner({
    gameId,
    onUpdateClick,
    newUi = false,
}: {
    gameId: string;
    onUpdateClick: () => void;
    newUi?: boolean;
}): React.JSX.Element | null {
    const { data, isFetching, refetch } = useTemplateUpdateCheck(gameId);
    if (!data) return null;
    if (data.error) return null;  // fail quiet in the banner; the modal surfaces errors when the user tries to act
    if (!data.hasUpdate) return null;

    if (newUi) {
        return (
            <div
                className="flex flex-none items-center justify-between py-2.5 pl-20 pr-6 f-body"
                style={{
                    background: 'var(--bg-elev-2)',
                    borderBottom: '1px solid var(--border-alpha-1)',
                    borderLeft: data.isBreaking
                        ? '3px solid rgba(245, 158, 11, 0.7)'
                        : '3px solid rgba(56, 189, 248, 0.5)',
                    color: 'var(--text-1)',
                }}
            >
                <span>
                    Template{' '}
                    <span className="font-medium">
                        v{data.current} → v{data.latest}
                    </span>
                    {' '}is available.
                    {data.isBreaking && (
                        <span
                            className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                            style={{ background: 'rgba(245,158,11,0.15)', color: 'rgb(252,211,77)' }}
                        >
                            Breaking
                        </span>
                    )}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="rounded-lg px-2 py-1 text-xs disabled:opacity-40"
                        style={{ color: 'var(--text-3)' }}
                        title="Re-check against the template repo"
                    >
                        {isFetching ? 'Checking…' : 'Check for updates'}
                    </button>
                    <button
                        onClick={onUpdateClick}
                        className="rounded-lg px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                        style={{
                            background: data.isBreaking ? 'rgba(217,119,6,0.9)' : 'var(--color-crucible-accent, #2145F7)',
                        }}
                    >
                        Update now
                    </button>
                </div>
            </div>
        );
    }

    const toneBg = data.isBreaking
        ? 'border-amber-500/40 bg-amber-500/15 text-amber-100'
        : 'border-sky-500/40 bg-sky-500/15 text-sky-100';

    return (
        // `pl-20` (5rem / 80px) on the left side keeps the banner content
        // out from underneath macOS's traffic-light window controls
        // (red/yellow/green at top-left of the BrowserWindow chrome),
        // which sit roughly 70-80px wide. Applied unconditionally —
        // Foundry is macOS-only post-PR #223 (alpha.56 dropped Intel),
        // so platform-detection would be overkill, and the extra
        // breathing room reads as deliberate on Windows builds too.
        // Same fix pattern as PreviewOverlay's header (PR #253 / #252).
        // See issue #271.
        <div className={`flex flex-none items-center justify-between border-b py-2.5 pl-20 pr-6 text-sm ${toneBg}`}>
            <span>
                Template{' '}
                <span className="font-medium">
                    v{data.current} → v{data.latest}
                </span>
                {' '}is available.
                {data.isBreaking && (
                    <span className="ml-2 rounded bg-amber-500/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-200">
                        Breaking
                    </span>
                )}
            </span>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="rounded-md px-2 py-1 text-xs text-white/60 hover:bg-white/5 hover:text-white/80 disabled:opacity-40"
                    title="Re-check against the template repo"
                >
                    {isFetching ? 'Checking…' : 'Check for updates'}
                </button>
                <button
                    onClick={onUpdateClick}
                    className={`rounded-md px-3 py-1 text-sm font-medium text-white hover:opacity-90 ${
                        data.isBreaking ? 'bg-amber-600' : 'bg-sky-600'
                    }`}
                >
                    Update now
                </button>
            </div>
        </div>
    );
}
